import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { GetCurrentCompetitionSurvey } from "../api/CompetitionSurveyApi";

const CompetitionSurvey = () => {
  const { t } = useTranslation("competition");

  const { token } = useSelector((state) => state.user);

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadSurvey = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const result = await GetCurrentCompetitionSurvey(token);

        setSurvey(result);
      } catch (err) {
        console.error("Competition Survey error:", err);

        setError(t("CompetitionSurveyLoadingError"));
      } finally {
        setLoading(false);
      }
    };

    loadSurvey();
  }, [token, t]);

  // ============================================================
  // Loading
  // ============================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 3,
        }}
      >
        <Stack
          spacing={2}
          alignItems="center"
          sx={{
            color: "text.secondary",
          }}
        >
          <CircularProgress size={40} thickness={4} />

          <Typography variant="body2" color="text.secondary">
            {t("Loading")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // ============================================================
  // API Error
  // ============================================================

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          px: {
            xs: 2,
            sm: 3,
            md: 4,
          },
          py: {
            xs: 3,
            md: 5,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 760,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 3,
                sm: 4,
              },
              py: 3,
              backgroundColor: "grey.50",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "error.light",
                  color: "error.dark",
                  flexShrink: 0,
                }}
              >
                <ErrorOutlineOutlinedIcon />
              </Box>

              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {t("CompetitionSurvey")}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("CompetitionSurveyDescription")}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              p: {
                xs: 3,
                sm: 4,
              },
            }}
          >
            <Alert
              severity="error"
              sx={{
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          </Box>
        </Paper>
      </Box>
    );
  }

  // ============================================================
  // Multiple Active Surveys
  // ============================================================

  if (survey?.status === "MultipleActive") {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          px: {
            xs: 2,
            sm: 3,
            md: 4,
          },
          py: {
            xs: 3,
            md: 5,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 760,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 3,
                sm: 4,
              },
              py: 3,
              backgroundColor: "grey.50",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "warning.light",
                  color: "warning.dark",
                  flexShrink: 0,
                }}
              >
                <ErrorOutlineOutlinedIcon />
              </Box>

              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {t("CompetitionSurvey")}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("CompetitionSurveyDescription")}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              p: {
                xs: 3,
                sm: 4,
              },
            }}
          >
            <Alert
              severity="warning"
              sx={{
                borderRadius: 2,
              }}
            >
              {t("CompetitionSurveyMultipleActive")}
            </Alert>
          </Box>
        </Paper>
      </Box>
    );
  }

  // ============================================================
  // No Active Survey
  // ============================================================

  if (survey?.status === "Closed") {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          px: {
            xs: 2,
            sm: 3,
            md: 4,
          },
          py: {
            xs: 3,
            md: 5,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 760,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: {
                xs: 3,
                sm: 4,
              },
              py: 3,
              backgroundColor: "grey.50",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  flexShrink: 0,
                }}
              >
                <AssessmentOutlinedIcon />
              </Box>

              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {t("CompetitionSurvey")}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("CompetitionSurveyDescription")}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Content */}
          <Box
            sx={{
              p: {
                xs: 3,
                sm: 4,
              },
            }}
          >
            <Alert
              severity="info"
              icon={<AccessTimeOutlinedIcon />}
              sx={{
                borderRadius: 2,
                alignItems: "flex-start",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                {t("CompetitionSurveyClosedTitle")}
              </Typography>

              <Typography variant="body2">
                {t("CompetitionSurveyClosedDescription")}
              </Typography>
            </Alert>
          </Box>
        </Paper>
      </Box>
    );
  }

  // ============================================================
  // Active Survey
  // ============================================================

  if (survey?.status === "Available" && survey?.surveyLink) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "calc(100vh - 120px)",
          minHeight: 600,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.default",
        }}
      >
        {/* Small page header */}
        <Box
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <AssessmentOutlinedIcon fontSize="small" />
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("CompetitionSurvey")}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {t("CompetitionSurveyActive")}
            </Typography>
          </Box>
        </Box>

        {/* Survey */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            backgroundColor: "background.paper",
          }}
        >
          <iframe
            src={survey.surveyLink}
            title={t("CompetitionSurvey")}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
          />
        </Box>
      </Box>
    );
  }

  // ============================================================
  // Unexpected Response
  // ============================================================

  return (
    <Box
      sx={{
        minHeight: "70vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
        py: {
          xs: 3,
          md: 5,
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 760,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: {
              xs: 3,
              sm: 4,
            },
          }}
        >
          <Alert
            severity="warning"
            sx={{
              borderRadius: 2,
            }}
          >
            {t("CompetitionSurveyUnavailable")}
          </Alert>
        </Box>
      </Paper>
    </Box>
  );
};

export default CompetitionSurvey;
